"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { MainMenuNav } from "./MainMenuNav";
import { MobileNav } from "./MobileNav";
import { ThemeToggle } from "./ThemeToggle";
import { ProudlyCanadianBadge } from "./ProudlyCanadianBadge";
import { LocaleSwitcher } from "./LocaleSwitcher";
import type { Country, Locale, LocaleContext } from "@/lib/i18n";
import { GEO_COOKIE, localizeHref } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

const LOGO_URL = "/cloudaxe_onlyLogo_Transparent_NoBuffer.png";

type SiteHeaderProps = LocaleContext & {
  subtitle: string;
  /** flyout = nested HIPAA submenu; flat = indented list (AppsNav). */
  appsMenu?: "flyout" | "flat";
  homePage?: boolean;
};

function readGeoCookie(): { country: Country | null; locale: Locale | null } {
  if (typeof document === "undefined") return { country: null, locale: null };
  try {
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${GEO_COOKIE}=`));
    const value = match?.split("=")[1];
    if (value === "ca-fr") return { country: "CA", locale: "fr" };
    if (value === "ca") return { country: "CA", locale: "en" };
    return { country: null, locale: null };
  } catch {
    return { country: null, locale: null };
  }
}

/**
 * Shared site header: logo, main menu, optional maple leaf + Contact, theme toggle.
 * On non-/ca routes, still shows the maple leaf when the geo cookie says Canada
 * (e.g. Apps pages visited after a geo redirect).
 */
export function SiteHeader({
  locale,
  country,
  subtitle,
  appsMenu = "flyout",
  homePage = false,
}: SiteHeaderProps) {
  const pathname = usePathname() || "/";
  const [cookieGeo, setCookieGeo] = useState<{
    country: Country | null;
    locale: Locale | null;
  }>({ country: null, locale: null });

  useEffect(() => {
    setCookieGeo(readGeoCookie());
  }, [pathname]);

  const effectiveCountry: Country =
    country === "CA" || cookieGeo.country === "CA" ? "CA" : "US";
  const effectiveLocale: Locale =
    country === "CA" ? locale : cookieGeo.locale ?? locale;

  const m = getMessages(effectiveLocale);
  const ctx = { locale: effectiveLocale, country: effectiveCountry };
  const homeHref = localizeHref("/", ctx);
  const contactHref = localizeHref("/contact", ctx);

  return (
    <>
      <header className="relative z-30 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <Link href={homeHref} className="group inline-flex items-center gap-2" aria-label={m.nav.homeAria}>
          <div className="logo-3d-wrap relative h-9 w-9 shrink-0 overflow-hidden">
            <div className="logo-3d-inner h-full w-full">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={LOGO_URL}
                alt=""
                width={36}
                height={36}
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide text-slate-900 dark:text-white">
              Cloud<span className="text-slate-500 dark:text-white/70">Axe</span>
            </div>
            <div className="text-xs text-slate-500 dark:text-white/60">{subtitle}</div>
          </div>
        </Link>

        <MainMenuNav appsMenu={appsMenu} />

        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          {effectiveCountry === "CA" ? (
            <ProudlyCanadianBadge label={m.nav.proudlyCanadian} />
          ) : null}
          <Link
            href={contactHref}
            className="hidden rounded-2xl bg-white/50 px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-white/40 backdrop-blur-xl transition-colors duration-200 hover:bg-white/60 md:inline-flex dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
          >
            {m.nav.contact}
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <MobileNav homePage={homePage} />
    </>
  );
}
